import { useEffect, useRef, useState } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import { api, formatApiError } from "@/lib/api";
import { Save, RotateCcw, Plus, Trash2, Code2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  { key: "home", label: "Home" },
  { key: "services", label: "Services" },
  { key: "service_models", label: "Service Models" },
  { key: "ai_in_delivery", label: "AI in Delivery" },
  { key: "engage", label: "Engage" },
  { key: "join_network", label: "Join Network" },
  { key: "contact", label: "Contact" },
  { key: "notifications", label: "Notifications" },
  { key: "security", label: "Security" },
  { key: "footer", label: "Footer" },
];

export default function AdminCMS() {
  const { user } = useAuth();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [savedAt, setSavedAt] = useState(null);
  const [tab, setTab] = useState("home");
  const [rawMode, setRawMode] = useState(false);
  const [rawDraft, setRawDraft] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/content");
      setContent(data.content);
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      let payload = content;
      if (rawMode) {
        try {
          const parsed = JSON.parse(rawDraft);
          payload = { ...content, [tab]: parsed };
          setContent(payload);
        } catch (parseErr) {
          setError("Invalid JSON: " + parseErr.message);
          setSaving(false);
          return;
        }
      }
      await api.put("/content", { content: payload });
      setSavedAt(new Date());
    } catch (e) { setError(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Reset all website content to defaults?")) return;
    try {
      await api.post("/content/reset");
      await load();
      setSavedAt(new Date());
      setRawMode(false);
    } catch (e) { setError(formatApiError(e)); }
  };

  const setField = (path, value) => {
    setContent((c) => {
      const next = structuredClone(c);
      let obj = next;
      for (let i = 0; i < path.length - 1; i++) obj = obj[path[i]];
      obj[path[path.length - 1]] = value;
      return next;
    });
  };

  const enterRawMode = () => {
    setRawDraft(JSON.stringify(content?.[tab] ?? {}, null, 2));
    setRawMode(true);
  };

  if (loading || !content) {
    return <AdminLayout title="Website content"><div className="text-sm text-muted-foreground">Loading…</div></AdminLayout>;
  }

  return (
    <AdminLayout
      title="Website content"
      subtitle="Edit all public copy. Changes appear on the live site immediately."
      actions={
        <>
          {user?.role === "admin" && (
            <button onClick={reset} className="btn-secondary" data-testid="cms-reset">
              <RotateCcw className="h-4 w-4" /> Reset to defaults
            </button>
          )}
          <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-60" data-testid="cms-save">
            <Save className="h-4 w-4" />
            {saving ? "Saving..." : "Save changes"}
          </button>
        </>
      }
    >
      {error && <div className="mb-4 text-sm text-destructive" data-testid="cms-error">{error}</div>}
      {savedAt && <div className="mb-4 text-xs font-mono text-execution" data-testid="cms-saved">Saved {savedAt.toLocaleTimeString()}</div>}

      <div className="flex flex-wrap gap-1 mb-4 border-b border-border">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => { setTab(t.key); setRawMode(false); }}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key ? "border-execution text-obsidian" : "border-transparent text-muted-foreground hover:text-obsidian"
            }`}
            data-testid={`cms-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2 py-1">
          <button
            onClick={() => (rawMode ? setRawMode(false) : enterRawMode())}
            className={`text-xs font-mono uppercase tracking-wider px-3 py-1 border ${rawMode ? "bg-execution text-white border-execution" : "border-border hover:border-obsidian"} transition-colors`}
            data-testid="cms-raw-toggle"
          >
            <Code2 className="h-3.5 w-3.5 inline mr-1.5" />
            {rawMode ? "Exit JSON" : "Edit raw JSON"}
          </button>
        </div>
      </div>

      <div className="border border-border bg-white p-6 space-y-5" data-testid="cms-editor">
        {rawMode ? (
          <RawEditor value={rawDraft} onChange={setRawDraft} />
        ) : (
          <>
            {tab === "home" && <HomeEditor content={content.home} setField={(k, v) => setField(["home", k], v)} />}
            {tab === "services" && <ServicesEditor content={content.services} setField={(k, v) => setField(["services", k], v)} setItems={(items) => setField(["services", "items"], items)} />}
            {tab === "service_models" && <ServiceModelsEditor content={content.service_models} setField={(p, v) => setField(["service_models", ...p], v)} />}
            {tab === "ai_in_delivery" && <AIDeliveryEditor content={content.ai_in_delivery} setField={(p, v) => setField(["ai_in_delivery", ...p], v)} />}
            {tab === "engage" && <SimpleEditor obj={content.engage} fields={["title", "subtitle", "success_title", "success_body"]} setField={(k, v) => setField(["engage", k], v)} />}
            {tab === "join_network" && <JoinNetworkEditor content={content.join_network} setField={(p, v) => setField(["join_network", ...p], v)} />}
            {tab === "contact" && <SimpleEditor obj={content.contact} fields={["title", "subtitle", "email", "address", "success_title", "success_body"]} setField={(k, v) => setField(["contact", k], v)} />}
            {tab === "notifications" && <NotificationsEditor content={content.notifications || {}} setField={(k, v) => setField(["notifications", k], v)} />}
            {tab === "security" && <SecurityEditor content={content.security || {}} setField={(k, v) => setField(["security", k], v)} />}
            {tab === "footer" && <SimpleEditor obj={content.footer} fields={["tagline"]} setField={(k, v) => setField(["footer", k], v)} />}
          </>
        )}
      </div>
    </AdminLayout>
  );
}

function Field({ label, value, onChange, textarea, testid }) {
  return (
    <label className="block">
      <span className="block text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-2">{label}</span>
      {textarea ? (
        <textarea className="input-swiss min-h-[100px]" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
      ) : (
        <input className="input-swiss" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
      )}
    </label>
  );
}

// Stable React keys for editable string/object rows that have no ids of their own.
function useRowKeys(length) {
  const keys = useRef([]);
  const seq = useRef(0);
  while (keys.current.length < length) keys.current.push(`row-${seq.current++}`);
  if (keys.current.length > length) keys.current.length = length;
  return { keys: keys.current, drop: (i) => keys.current.splice(i, 1) };
}

function ListEditor({ label, items, onChange }) {
  const rowKeys = useRowKeys(items.length);
  const update = (i, v) => { const next = [...items]; next[i] = v; onChange(next); };
  const remove = (i) => { rowKeys.drop(i); onChange(items.filter((_, idx) => idx !== i)); };
  const add = () => onChange([...items, ""]);
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">{label}</span>
        <button type="button" onClick={add} className="btn-secondary text-xs py-1.5 px-3">
          <Plus className="h-3.5 w-3.5" /> Add
        </button>
      </div>
      <div className="space-y-2">
        {items.map((item, i) => (
          <div key={rowKeys.keys[i]} className="flex gap-2">
            <input className="input-swiss" value={item} onChange={(e) => update(i, e.target.value)} />
            <button type="button" onClick={() => remove(i)} className="h-auto w-10 inline-flex items-center justify-center border border-border hover:bg-destructive hover:text-white hover:border-destructive transition-colors">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function SimpleEditor({ obj, fields, setField }) {
  return (
    <>
      {fields.map((f) => (
        <Field
          key={f} label={f.replace(/_/g, " ")} value={obj?.[f]}
          onChange={(v) => setField(f, v)}
          textarea={f === "body" || f === "subtitle" || f === "address" || f === "tagline" || f === "success_body"}
          testid={`cms-field-${f}`}
        />
      ))}
    </>
  );
}

function HomeEditor({ content, setField }) {
  return (
    <>
      <Field label="Hero eyebrow" value={content.hero_eyebrow} onChange={(v) => setField("hero_eyebrow", v)} testid="cms-home-eyebrow" />
      <Field label="Hero title" value={content.hero_title} onChange={(v) => setField("hero_title", v)} textarea testid="cms-home-title" />
      <Field label="Hero subtitle" value={content.hero_subtitle} onChange={(v) => setField("hero_subtitle", v)} textarea testid="cms-home-subtitle" />
      <Field label="AI subline (under hero)" value={content.ai_subline} onChange={(v) => setField("ai_subline", v)} textarea testid="cms-home-ai-subline" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Primary CTA" value={content.hero_primary_cta} onChange={(v) => setField("hero_primary_cta", v)} testid="cms-home-cta1" />
        <Field label="Secondary CTA" value={content.hero_secondary_cta} onChange={(v) => setField("hero_secondary_cta", v)} testid="cms-home-cta2" />
      </div>

      <div className="pt-4 border-t border-border space-y-4">
        <Field label="What we do — title" value={content.what_we_do_title} onChange={(v) => setField("what_we_do_title", v)} />
        <Field label="What we do — body" value={content.what_we_do_body} onChange={(v) => setField("what_we_do_body", v)} textarea />
      </div>

      <div className="pt-4 border-t border-border space-y-4">
        <Field label="How we operate — title" value={content.operates_title} onChange={(v) => setField("operates_title", v)} testid="cms-home-operates-title" />
        <Field label="How we operate — intro" value={content.operates_intro} onChange={(v) => setField("operates_intro", v)} textarea testid="cms-home-operates-intro" />
        <ListEditor
          label="How we operate — outcomes (chips)"
          items={content.operates_outcomes || []}
          onChange={(items) => setField("operates_outcomes", items)}
        />
        <p className="text-xs text-muted-foreground">For &lsquo;operates_pillars&rsquo; (title + body objects), use <strong>Edit raw JSON</strong> mode.</p>
      </div>

      <div className="pt-4 border-t border-border space-y-4">
        <Field label="Evolution — title" value={content.evolution_title} onChange={(v) => setField("evolution_title", v)} testid="cms-home-evolution-title" />
        <Field label="Evolution — lead line" value={content.evolution_lead} onChange={(v) => setField("evolution_lead", v)} testid="cms-home-evolution-lead" />
        <Field label="Evolution — body" value={content.evolution_body} onChange={(v) => setField("evolution_body", v)} textarea testid="cms-home-evolution-body" />
        <Field label="Evolution — combine intro" value={content.evolution_combine_intro} onChange={(v) => setField("evolution_combine_intro", v)} textarea />
        <ListEditor
          label="Evolution — improvement outcomes"
          items={content.evolution_outcomes || []}
          onChange={(items) => setField("evolution_outcomes", items)}
        />
        <Field label="Evolution — closing line" value={content.evolution_close} onChange={(v) => setField("evolution_close", v)} textarea />
      </div>

      <div className="pt-4 border-t border-border space-y-4">
        <Field label="Future of Delivery — title" value={content.future_title} onChange={(v) => setField("future_title", v)} testid="cms-home-future-title" />
        <Field label="Future of Delivery — body" value={content.future_body} onChange={(v) => setField("future_body", v)} textarea testid="cms-home-future-body" />
        <ListEditor
          label="Future of Delivery — pillars"
          items={content.future_pillars || []}
          onChange={(items) => setField("future_pillars", items)}
        />
      </div>

      <div className="pt-4 border-t border-border space-y-4">
        <Field label="Models — title" value={content.models_title} onChange={(v) => setField("models_title", v)} />
        <Field label="Models — intro" value={content.models_intro} onChange={(v) => setField("models_intro", v)} textarea />
        <p className="text-xs text-muted-foreground">For detailed model bullets, use <strong>Edit raw JSON</strong> mode.</p>
      </div>

      <div className="pt-4 border-t border-border space-y-4">
        <Field label="AI — title" value={content.ai_title} onChange={(v) => setField("ai_title", v)} />
        <Field label="AI — body" value={content.ai_body} onChange={(v) => setField("ai_body", v)} textarea />
        <p className="text-xs text-muted-foreground">For AI pillars list, use <strong>Edit raw JSON</strong> mode.</p>
      </div>

      <div className="pt-4 border-t border-border space-y-4">
        <Field label="When to engage — title" value={content.when_engage_title} onChange={(v) => setField("when_engage_title", v)} />
        <ListEditor
          label="When to engage items"
          items={content.when_engage_items || []}
          onChange={(items) => setField("when_engage_items", items)}
        />
      </div>
    </>
  );
}

function ServicesEditor({ content, setField, setItems }) {
  const items = content?.items || [];
  const updateItem = (i, key, value) => {
    const next = [...items]; next[i] = { ...next[i], [key]: value }; setItems(next);
  };
  const addItem = () => setItems([...items, { key: `service_${items.length + 1}`, title: "", outcome: "", staff_aug: "", delivery_services: "" }]);
  const rowKeys = useRowKeys(items.length);
  const removeItem = (i) => { rowKeys.drop(i); setItems(items.filter((_, idx) => idx !== i)); };

  return (
    <>
      <Field label="Page title" value={content.page_title} onChange={(v) => setField("page_title", v)} />
      <Field label="Page subtitle" value={content.page_subtitle} onChange={(v) => setField("page_subtitle", v)} textarea />
      <div className="pt-4 border-t border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="overline">Service items</div>
          <button type="button" onClick={addItem} className="btn-secondary text-xs">
            <Plus className="h-4 w-4" /> Add service
          </button>
        </div>
        <div className="space-y-4">
          {items.map((it, i) => (
            <div key={rowKeys.keys[i]} className="border border-border p-4 bg-muted/30">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Service #{i + 1}</div>
                <button type="button" onClick={() => removeItem(i)} className="h-7 w-7 inline-flex items-center justify-center border border-border hover:bg-destructive hover:text-white hover:border-destructive transition-colors">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="space-y-3">
                <Field label="Title" value={it.title} onChange={(v) => updateItem(i, "title", v)} />
                <Field label="Outcome statement" value={it.outcome} onChange={(v) => updateItem(i, "outcome", v)} textarea />
                <Field label="Under Staff Augmentation" value={it.staff_aug} onChange={(v) => updateItem(i, "staff_aug", v)} textarea />
                <Field label="Under Delivery Services" value={it.delivery_services} onChange={(v) => updateItem(i, "delivery_services", v)} textarea />
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function ServiceModelsEditor({ content, setField }) {
  return (
    <>
      <Field label="Page title" value={content?.page_title} onChange={(v) => setField(["page_title"], v)} />
      <Field label="Page subtitle" value={content?.page_subtitle} onChange={(v) => setField(["page_subtitle"], v)} textarea />

      <div className="pt-4 border-t border-border space-y-3">
        <div className="overline">Staff Augmentation</div>
        <Field label="Title" value={content?.staff_aug?.title} onChange={(v) => setField(["staff_aug", "title"], v)} />
        <Field label="Tagline" value={content?.staff_aug?.tagline} onChange={(v) => setField(["staff_aug", "tagline"], v)} />
        <Field label="Intro" value={content?.staff_aug?.intro} onChange={(v) => setField(["staff_aug", "intro"], v)} textarea />
        <ListEditor label="Highlights" items={content?.staff_aug?.highlights || []} onChange={(items) => setField(["staff_aug", "highlights"], items)} />
        <Field label="Fit statement" value={content?.staff_aug?.fit} onChange={(v) => setField(["staff_aug", "fit"], v)} textarea />
      </div>

      <div className="pt-4 border-t border-border space-y-3">
        <div className="overline">Delivery Services</div>
        <Field label="Title" value={content?.delivery_services?.title} onChange={(v) => setField(["delivery_services", "title"], v)} />
        <Field label="Tagline" value={content?.delivery_services?.tagline} onChange={(v) => setField(["delivery_services", "tagline"], v)} />
        <Field label="Intro" value={content?.delivery_services?.intro} onChange={(v) => setField(["delivery_services", "intro"], v)} textarea />
        <ListEditor label="Highlights" items={content?.delivery_services?.highlights || []} onChange={(items) => setField(["delivery_services", "highlights"], items)} />
        <Field label="Fit statement" value={content?.delivery_services?.fit} onChange={(v) => setField(["delivery_services", "fit"], v)} textarea />
      </div>

      <div className="pt-4 border-t border-border">
        <p className="text-xs text-muted-foreground">For the comparison table, use <strong>Edit raw JSON</strong> mode.</p>
      </div>
    </>
  );
}

function AIDeliveryEditor({ content, setField }) {
  return (
    <>
      <Field label="Page title" value={content?.page_title} onChange={(v) => setField(["page_title"], v)} />
      <Field label="Page subtitle" value={content?.page_subtitle} onChange={(v) => setField(["page_subtitle"], v)} textarea />
      <Field label="Intro paragraph" value={content?.intro} onChange={(v) => setField(["intro"], v)} textarea />
      <Field label="Outcomes section title" value={content?.outcome_title} onChange={(v) => setField(["outcome_title"], v)} />
      <ListEditor label="Outcomes (measurable)" items={content?.outcomes || []} onChange={(items) => setField(["outcomes"], items)} />
      <p className="text-xs text-muted-foreground">For AI capabilities list (with titles + bodies), use <strong>Edit raw JSON</strong> mode.</p>
    </>
  );
}

function JoinNetworkEditor({ content, setField }) {
  return (
    <>
      <Field label="Page title" value={content?.title} onChange={(v) => setField(["title"], v)} />
      <Field label="Page subtitle" value={content?.subtitle} onChange={(v) => setField(["subtitle"], v)} textarea />
      <Field label="Intro paragraph" value={content?.intro} onChange={(v) => setField(["intro"], v)} textarea />
      <Field label="How it works — title" value={content?.model_title} onChange={(v) => setField(["model_title"], v)} />
      <ListEditor label="How it works — bullet points" items={content?.model_points || []} onChange={(items) => setField(["model_points"], items)} />
      <ListEditor label="Disciplines (display chips)" items={content?.disciplines || []} onChange={(items) => setField(["disciplines"], items)} />
      <Field label="Submission success — title" value={content?.success_title} onChange={(v) => setField(["success_title"], v)} testid="cms-network-success-title" />
      <Field label="Submission success — body" value={content?.success_body} onChange={(v) => setField(["success_body"], v)} textarea testid="cms-network-success-body" />
    </>
  );
}

function RawEditor({ value, onChange }) {
  return (
    <div data-testid="cms-raw-editor">
      <div className="overline mb-2">Raw JSON for selected section</div>
      <p className="text-xs text-muted-foreground mb-3">
        Edit the section as JSON. Save will validate and apply changes.
      </p>
      <textarea
        className="input-swiss font-mono text-xs min-h-[500px] resize-y"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        spellCheck={false}
      />
    </div>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-3 py-3 border-b border-border" data-testid={testid}>
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 accent-execution"
      />
      <span className="text-sm">{label}</span>
    </label>
  );
}

function NotificationsEditor({ content, setField }) {
  return (
    <div className="space-y-6">
      <EmailTestPanel />
      <div>
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-3">
          Administrator alerts
        </p>
        <Field
          label="Administrator notification email"
          value={content.admin_email}
          onChange={(v) => setField("admin_email", v)}
          testid="cms-notif-admin-email"
        />
        <Field
          label="Admin dashboard URL (used in 'View Full Submission' links)"
          value={content.admin_dashboard_url}
          onChange={(v) => setField("admin_dashboard_url", v)}
          testid="cms-notif-admin-dashboard-url"
        />
        <div className="mt-4">
          <Toggle
            label="Send admin alert when a candidate registers"
            checked={content.notify_admin_on_candidate}
            onChange={(v) => setField("notify_admin_on_candidate", v)}
            testid="cms-notif-toggle-admin-candidate"
          />
          <Toggle
            label="Send admin alert when a client enquiry is submitted"
            checked={content.notify_admin_on_enquiry}
            onChange={(v) => setField("notify_admin_on_enquiry", v)}
            testid="cms-notif-toggle-admin-enquiry"
          />
          <Toggle
            label="Send admin alert when a contact request is submitted"
            checked={content.notify_admin_on_contact}
            onChange={(v) => setField("notify_admin_on_contact", v)}
            testid="cms-notif-toggle-admin-contact"
          />
        </div>
      </div>

      <div>
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-3">
          Auto-responses to submitters
        </p>
        <Toggle
          label="Send auto-response email to candidates"
          checked={content.send_candidate_auto_response}
          onChange={(v) => setField("send_candidate_auto_response", v)}
          testid="cms-notif-toggle-auto-candidate"
        />
        <Field
          label="Candidate auto-response — subject"
          value={content.candidate_auto_subject}
          onChange={(v) => setField("candidate_auto_subject", v)}
          testid="cms-notif-cand-subject"
        />
        <Field
          label="Candidate auto-response — body"
          value={content.candidate_auto_body}
          onChange={(v) => setField("candidate_auto_body", v)}
          textarea
          testid="cms-notif-cand-body"
        />
        <div className="mt-6">
          <Toggle
            label="Send auto-response email to clients (enquiry)"
            checked={content.send_client_auto_response}
            onChange={(v) => setField("send_client_auto_response", v)}
            testid="cms-notif-toggle-auto-client"
          />
          <Field
            label="Client auto-response — subject"
            value={content.client_auto_subject}
            onChange={(v) => setField("client_auto_subject", v)}
            testid="cms-notif-client-subject"
          />
          <Field
            label="Client auto-response — body"
            value={content.client_auto_body}
            onChange={(v) => setField("client_auto_body", v)}
            textarea
            testid="cms-notif-client-body"
          />
        </div>
      </div>

      <div className="text-xs text-muted-foreground border-t border-border pt-4">
        <p className="font-medium text-foreground mb-1">Tokens you can use in template bodies:</p>
        <p>Candidate: <code>{"{{name}}"}</code>, <code>{"{{email}}"}</code>, <code>{"{{discipline}}"}</code>, <code>{"{{availability}}"}</code></p>
        <p>Client: <code>{"{{name}}"}</code>, <code>{"{{organisation}}"}</code>, <code>{"{{email}}"}</code></p>
      </div>
    </div>
  );
}


function SecurityEditor({ content, setField }) {
  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-3">
          Spam scoring & rate limiting
        </p>
        <Field
          label="Minimum form completion time (seconds) — 0 to disable"
          value={String(content.min_completion_seconds ?? 0)}
          onChange={(v) => setField("min_completion_seconds", Number(v) || 0)}
          testid="cms-sec-min-completion"
        />
        <Field
          label="Spam score rejection threshold (0–100)"
          value={String(content.spam_score_threshold ?? 70)}
          onChange={(v) => setField("spam_score_threshold", Number(v) || 70)}
          testid="cms-sec-spam-threshold"
        />
        <Field
          label="Duplicate detection window (seconds) — 0 to disable"
          value={String(content.duplicate_window_seconds ?? 300)}
          onChange={(v) => setField("duplicate_window_seconds", Number(v) || 0)}
          testid="cms-sec-duplicate-window"
        />
        <Field
          label="Per-IP rate limit — max submissions"
          value={String(content.rate_limit_max ?? 5)}
          onChange={(v) => setField("rate_limit_max", Number(v) || 5)}
          testid="cms-sec-rate-max"
        />
        <Field
          label="Per-IP rate limit — window (seconds)"
          value={String(content.rate_limit_window_seconds ?? 3600)}
          onChange={(v) => setField("rate_limit_window_seconds", Number(v) || 3600)}
          testid="cms-sec-rate-window"
        />
      </div>

      <div>
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-3">
          Cloudflare Turnstile (optional)
        </p>
        <p className="text-xs text-muted-foreground mb-3 max-w-2xl">
          Free invisible-by-default CAPTCHA. Get your keys at{" "}
          <a href="https://dash.cloudflare.com/?to=/:account/turnstile" target="_blank" rel="noreferrer" className="text-execution underline">
            dash.cloudflare.com → Turnstile
          </a>
          . Add the site key + secret key, then toggle Enabled. Frontend will render the widget automatically.
        </p>
        <label className="flex items-center gap-3 py-3 border-b border-border" data-testid="cms-sec-turnstile-toggle">
          <input
            type="checkbox"
            checked={!!content.turnstile_enabled}
            onChange={(e) => setField("turnstile_enabled", e.target.checked)}
            className="h-4 w-4 accent-execution"
          />
          <span className="text-sm">Enable Cloudflare Turnstile</span>
        </label>
        <Field
          label="Turnstile site key (public)"
          value={content.turnstile_site_key || ""}
          onChange={(v) => setField("turnstile_site_key", v)}
          testid="cms-sec-turnstile-site"
        />
        <Field
          label="Turnstile secret key"
          value={content.turnstile_secret_key || ""}
          onChange={(v) => setField("turnstile_secret_key", v)}
          testid="cms-sec-turnstile-secret"
        />
      </div>

      <div>
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-3">
          Email verification
        </p>
        <label className="flex items-center gap-3 py-3 border-b border-border" data-testid="cms-sec-email-verify-toggle">
          <input
            type="checkbox"
            checked={!!content.email_verification_enabled}
            onChange={(e) => setField("email_verification_enabled", e.target.checked)}
            className="h-4 w-4 accent-execution"
          />
          <span className="text-sm">Send candidate email-verification magic links (requires SMTP)</span>
        </label>
        <p className="text-xs text-muted-foreground mt-2">
          When enabled, new candidate registrations receive a verification link before being shown as &ldquo;verified&rdquo; in the admin. Existing submissions are not affected.
        </p>
      </div>

      <div>
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-3">
          Block lists (comma separated)
        </p>
        <Field
          label="Blocked email domains"
          value={content.blocked_email_domains || ""}
          onChange={(v) => setField("blocked_email_domains", v)}
          textarea
          testid="cms-sec-blocked-domains"
        />
        <Field
          label="Blocked IP addresses"
          value={content.blocked_ip_addresses || ""}
          onChange={(v) => setField("blocked_ip_addresses", v)}
          textarea
          testid="cms-sec-blocked-ips"
        />
      </div>
    </div>
  );
}


function EmailTestPanel() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [recipient, setRecipient] = useState("");

  const run = async () => {
    setRunning(true);
    setResult(null);
    setError("");
    try {
      const payload = { send_email: sendEmail };
      if (recipient.trim()) payload.to = recipient.trim();
      const { data } = await api.post("/admin/email-test", payload);
      setResult(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setRunning(false);
    }
  };

  const authBadge = (ok) =>
    ok
      ? <span className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider bg-emerald-100 text-emerald-700">PASS</span>
      : <span className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider bg-red-100 text-red-700">FAIL</span>;

  return (
    <div className="border border-border bg-zinc-50 p-5" data-testid="email-test-panel">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-1">
            Email delivery test — Resend
          </p>
          <p className="text-sm text-muted-foreground">
            Validates the Resend API key, confirms the sender domain is verified, and
            (optionally) sends a real test email to the configured admin inbox.
            Result is recorded in <code>email_logs</code>.
          </p>
        </div>
        <button
          type="button"
          onClick={run}
          disabled={running}
          data-testid="email-test-run"
          className="btn-primary text-xs whitespace-nowrap"
        >
          {running ? "Testing..." : "Send test email"}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-3">
        <label className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-muted-foreground">
          <input
            type="checkbox"
            checked={sendEmail}
            onChange={(e) => setSendEmail(e.target.checked)}
            data-testid="email-test-send-toggle"
          />
          Send real test email
        </label>
        <input
          type="email"
          placeholder="Override recipient (optional)"
          value={recipient}
          onChange={(e) => setRecipient(e.target.value)}
          data-testid="email-test-recipient"
          className="text-xs px-2 py-1 border border-border bg-white w-72"
        />
      </div>

      {error && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 p-2" data-testid="email-test-error">
          {error}
        </div>
      )}

      {result && (
        <div className="space-y-3 mt-2" data-testid="email-test-result">
          <div className="flex flex-wrap items-center gap-3 text-xs font-mono uppercase tracking-wider">
            <span>API key + domain: {authBadge(result.auth_ok)}</span>
            {result.test_email && !result.test_email.skipped && (
              <span>Send: {authBadge(result.test_email.status === "sent")}</span>
            )}
            {result.test_email?.skipped && (
              <span className="text-muted-foreground">Send skipped — {result.test_email.reason}</span>
            )}
          </div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs font-mono">
            <div className="text-muted-foreground">Provider</div>
            <div>{result.provider}</div>
            <div className="text-muted-foreground">From</div>
            <div>{result.from}</div>
            <div className="text-muted-foreground">Sender domain</div>
            <div data-testid="email-test-domain">
              {result.resend?.from_domain}
              {result.resend?.verified_match
                ? <span className="ml-2 text-emerald-700">verified ✓</span>
                : <span className="ml-2 text-red-700">NOT verified</span>}
            </div>
            <div className="text-muted-foreground">Recipient</div>
            <div>{result.recipient}</div>
            {result.test_email?.message_id && (
              <>
                <div className="text-muted-foreground">Resend message ID</div>
                <div className="break-all">{result.test_email.message_id}</div>
              </>
            )}
            {result.auth_error && (
              <>
                <div className="text-muted-foreground">Auth error</div>
                <div className="text-red-700 break-all">{result.auth_error}</div>
              </>
            )}
            {result.test_email?.error && (
              <>
                <div className="text-muted-foreground">Send error</div>
                <div className="text-red-700 break-all">{result.test_email.error}</div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
